<template>
  <div class="sp-reorder-list" :class="classModifiers">
    <div ref="items" class="sp-reorder-list__items" role="list" :aria-label="accessibleName">
      <slot @slotchange="syncRows" />
    </div>
    <div class="sp-reorder-list__status" aria-live="polite" aria-atomic="true">{{ status }}</div>
  </div>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { useSortable } from "@vueuse/integrations/useSortable";
import { computed, nextTick, ref, useHost, useTemplateRef, watch } from "vue";
import { tAccessibleName } from "#ce/shared/components-i18n";
import { getSlotContentByTagName } from "#ce/utils/slots";
import { toBoolean } from "#ce/utils/props";
import { props as reorderListProps } from "./props.js";

const ROW_TAG = "sp-reorder-list-item";

const props = defineProps(reorderListProps);
const emit = defineEmits(["update-order"]);

const host = useHost();
const items = useTemplateRef("items");
const status = ref("");
let rowsAtDragStart = [];

const readonly = computed(() => toBoolean(props.readonly));

const classModifiers = computed(() => ({
  [`--variant-${props.variant}`]: true,
  [`--density-${props.density}`]: props.density !== "default",
  "--controls-align-start": props.controlsAlign === "start",
  "--readonly": readonly.value,
}));

// The page owns the rows, so the list moves the row elements themselves. `useSortable`'s default
// `onUpdate` puts the dragged row back and reorders the array passed in. `handleDrop` replaces it,
// so that array is never read.
const { option } = useSortable(host, [], {
  draggable: ROW_TAG,
  animation: 150,
  filter: ".sp-reorder-list-item__moves",
  preventOnFilter: false,
  // Touch drags go through touch events, so a touch the system interrupts ends the drag with
  // `touchcancel`. Through pointer events, SortableJS never hears that cancel in desktop browsers.
  supportPointer: false,
  // On a touch screen a row drags only after a short press, so a swipe that starts on a row still
  // scrolls the page.
  delay: 200,
  delayOnTouchOnly: true,
  disabled: readonly.value,
  onStart: rememberRows,
  onUpdate: handleDrop,
  onEnd: finishDrag,
});

useEventListener(host, "request-move", handleMoveRequest);
useEventListener(host, "change", keepSortableChangeInside);
watch(readonly, applyReadonly);

// Returns the rows in order, leaving out the floating copy of the row SortableJS adds to the list
// during a touch drag.
function rows() {
  return getSlotContentByTagName(items, ROW_TAG.toUpperCase()).filter(
    (row) => !row.classList.contains("sortable-fallback"),
  );
}

function syncRows() {
  const all = rows();

  all.forEach((row, index) => {
    row.toggleAttribute("first", index === 0);
    row.toggleAttribute("last", index === all.length - 1);
    row.toggleAttribute("readonly", readonly.value);
  });
}

function applyReadonly(value) {
  option("disabled", value);
  syncRows();
}

function handleMoveRequest(event) {
  event.stopPropagation();

  const row = event.target;
  const [step] = event.detail;
  const all = rows();
  const neighbor = all[all.indexOf(row) + step];

  if (!neighbor) {
    return;
  }

  if (step < 0) {
    neighbor.before(row);
  } else {
    neighbor.after(row);
  }

  announceMove(row);
}

function rememberRows() {
  rowsAtDragStart = rows();
}

// SortableJS calls `onUpdate` for any drag that leaves the row in a new place, including one that
// ended outside the list.
function handleDrop({ item, originalEvent }) {
  if (endedOnList(originalEvent)) {
    announceMove(item);
  } else {
    putBack(item);
  }
}

// Returns whether the drag ended on the list. `event` is the event that ended the drag:
//   - `drop`: a mouse drag dropped on the list, or on another drop target on the page
//   - `dragend` alone: a mouse drag canceled with Escape, or dropped where nothing accepts it
//   - `touchcancel`: a touch drag the system interrupted
//   - `touchend`: a touch drag, ended where the finger lifts
function endedOnList(event) {
  if (event.type === "drop") {
    return event.composedPath().includes(host);
  }

  if (event.type === "dragend" || event.type === "touchcancel") {
    return false;
  }

  const { clientX, clientY } = event.changedTouches?.[0] ?? event;

  // Asks what shows at the point, so rows scrolled out of view in a panel don't count.
  return host.contains(host.getRootNode().elementFromPoint(clientX, clientY));
}

// Puts the row back before the first row that followed it when the drag started and is still in the
// list, or last when none is left. The page may have added or removed rows during the drag.
// SortableJS also calls `onUpdate` for a press that never started a drag, when the page moved the
// row meanwhile; no rows are remembered then, and the row stays where it is.
function putBack(row) {
  const start = rowsAtDragStart.indexOf(row);

  if (start === -1) {
    return;
  }

  const others = rows().filter((each) => each !== row);
  const following = rowsAtDragStart.slice(start + 1);
  const next = following.find((each) => others.includes(each));

  if (next) {
    next.before(row);
  } else {
    others.at(-1)?.after(row);
  }
}

// Reports the new order, then announces the move. The status is emptied first, so a move that
// repeats the last announcement still changes the text and is read out.
async function announceMove(row) {
  const all = rows();

  status.value = "";
  emit(
    "update-order",
    all.map((each) => each.value ?? each.getAttribute("value")),
  );
  await nextTick();
  status.value = tAccessibleName("reorder_list_moved", {
    label: row.label ?? row.getAttribute("label"),
    position: all.indexOf(row) + 1,
    count: all.length,
  });
}

function finishDrag(event) {
  rowsAtDragStart = [];
  keepNextToRows(event);
}

// Moves the dragged row back against the row before it. A page that renders the rows with Vue's
// `v-for` has marker text nodes before the first row and after the last. SortableJS can append a
// row dragged past the last row, which puts it after the end marker. The page's later renders
// place rows relative to the markers, so the row goes back inside them.
function keepNextToRows({ item }) {
  const before = item.previousElementSibling;

  if (before && before.nextSibling !== item) {
    before.after(item);
  }
}

// Stops the bubbling `change` SortableJS dispatches from the list each time the dragged row moves,
// which a form's change tracking counts as an edit. A `change` from a control inside a row has that
// control as its target and still bubbles.
function keepSortableChangeInside(event) {
  if (event.target === host) {
    event.stopPropagation();
  }
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-reorder-list {
  --reorder-list-gap: 0;
  --reorder-list-controls-align: center;
  --reorder-list-controls-height: auto;
  --reorder-list-item-padding-block: var(--sp-comp-reorder-list-item-padding-block, var(--sp-ref-scale-12));
  --reorder-list-item-padding-inline: var(--sp-comp-reorder-list-item-padding-inline, var(--sp-ref-scale-16));
  --reorder-list-item-column-gap: var(--sp-comp-reorder-list-item-column-gap, var(--sp-ref-scale-16));
  --reorder-list-item-border-radius: var(--sp-comp-reorder-list-item-border-radius, 0);
  --reorder-list-border: var(--sp-sys-border-width-default) solid
    var(--sp-comp-reorder-list-border-color, var(--sp-sys-color-outline-variant));

  &.--variant-outlined {
    --reorder-list-gap: var(--sp-comp-reorder-list-gap, var(--sp-ref-scale-8));
    --reorder-list-item-border-radius: var(--sp-comp-reorder-list-item-border-radius, var(--sp-ref-border-radius-sm));
  }

  &.--variant-plain {
    --reorder-list-item-border-radius: var(--sp-comp-reorder-list-item-border-radius, var(--sp-ref-border-radius-xs));
  }

  &.--density-comfortable {
    --reorder-list-item-padding-block: var(
      --sp-comp-reorder-list-comfortable-item-padding-block,
      var(--sp-ref-scale-10)
    );
    --reorder-list-item-padding-inline: var(
      --sp-comp-reorder-list-comfortable-item-padding-inline,
      var(--sp-ref-scale-16)
    );
    --reorder-list-item-column-gap: var(--sp-comp-reorder-list-comfortable-item-column-gap, var(--sp-ref-scale-8));
  }

  &.--density-compact {
    --reorder-list-item-padding-block: var(--sp-comp-reorder-list-compact-item-padding-block, var(--sp-ref-scale-4));
    --reorder-list-item-padding-inline: var(--sp-comp-reorder-list-compact-item-padding-inline, var(--sp-ref-scale-8));
    --reorder-list-item-column-gap: var(--sp-comp-reorder-list-compact-item-column-gap, var(--sp-ref-scale-8));
  }

  &.--controls-align-start {
    --reorder-list-controls-align: flex-start;
    --reorder-list-controls-height: var(--sp-comp-reorder-list-first-line-height, 1lh);
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: var(--reorder-list-gap);
  }

  slot::slotted(sp-reorder-list-item) {
    border-radius: var(--reorder-list-item-border-radius);
  }

  &.--variant-outlined slot::slotted(sp-reorder-list-item) {
    border: var(--reorder-list-border);
    background: var(--sp-comp-reorder-list-item-background-color, var(--sp-sys-color-surface));
  }

  &.--variant-divided slot::slotted(sp-reorder-list-item:not([first])) {
    border-top: var(--reorder-list-border);
  }

  &:not(.--readonly) slot::slotted(sp-reorder-list-item) {
    cursor: grab;
  }

  &:not(.--readonly) slot::slotted(sp-reorder-list-item:hover) {
    background: var(--sp-comp-reorder-list-item-hover-background-color, var(--sp-sys-color-surface-container));
  }

  slot::slotted(.sortable-ghost) {
    opacity: var(--sp-comp-reorder-list-dragging-opacity, 0.4);
  }

  &__status {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}
</style>
